import {
  Button,
  Icon,
  Spacer,
  Tab,
  TextCaption,
  TextTitle,
  Tooltip,
} from '@pypestream/design-system';
import { FC, useEffect, useState } from 'react';

import { ActionChanges, AgentChanges, CountChips } from './diff-parts';
import { describeActionChanges } from './action-file-diff';
import { useActionFileDiff } from './use-action-file-diff';
import { DiffView } from './diff-view';
import {
  summarizeDiff,
  totalChanges,
  type SolutionDiff,
} from './solution-diff';
import { BobcatTabGroup } from '../../components/bobcat-tab-group';
import { useReleaseNotes } from './use-release-notes';
import { ValidationCallout } from './validation-callout';

export type DeployEnv = 'sandbox' | 'live';

interface PublishPanelProps {
  draftVersion: string;
  /** The version the draft is compared with, or null on a first publish. */
  baselineVersion: string | null;
  baselineEnvLabel: string;
  diff: SolutionDiff | null;
  diffError: string | null;
  env: DeployEnv;
  onEnvChange: (env: DeployEnv) => void;
  envs: string[];
  canPublish: boolean;
  publishTooltip?: string;
  onPublish: () => void;
  /**
   * Live takes the version tested in Sandbox, not the draft: set, this is
   * that version ("v3"), and Publish promotes it. Null publishes the draft.
   */
  promoteVersion: string | null;
  /** What's in Sandbox, which Validate tests. */
  sandboxVersion: string | null;
  /** The Sandbox version is already the live one: nothing to promote. */
  alreadyLive: boolean;
  /** Nodes the draft has changed since the promoted version. */
  draftAhead: number;
  promoteError: string | null;
  onPromote: () => void;
  isViewer: boolean;
}

type PanelTab = 'notes' | 'diff';

/** "Action code": the scripts this publish adds, edits or removes. */
const ActionCodeSection: FC<{
  state: ReturnType<typeof useActionFileDiff>;
  baseline: string | null;
}> = ({ state, baseline }) => {
  if (state.status === 'ready' && !state.changes.length) return null;
  return (
    <>
      <Spacer size="small" />
      <div className="c-deploy-eyebrow">Action code</div>
      {state.status === 'loading' && (
        <TextCaption variant="secondary">Comparing action code…</TextCaption>
      )}
      {state.status === 'error' && (
        <TextCaption variant="danger">
          Couldn&apos;t compare action code
          {baseline ? ` with ${baseline}` : ''}: {state.error}
        </TextCaption>
      )}
      {state.status === 'ready' && <ActionChanges changes={state.changes} />}
    </>
  );
};

/**
 * Deploy's release panel (prototype `.dp-panel`): what this publish changes
 * compared with the version already in the chosen environment, and the
 * Publish action itself.
 *
 * Release notes are AI-drafted from the diff (`use-release-notes.ts`), with
 * the plain summary standing in until they arrive or when they can't be
 * written; beside them, what Validate says about the solution.
 */
export const PublishPanel: FC<PublishPanelProps> = ({
  draftVersion,
  baselineVersion,
  baselineEnvLabel,
  diff,
  diffError,
  env,
  onEnvChange,
  envs,
  canPublish,
  publishTooltip,
  onPublish,
  promoteVersion,
  sandboxVersion,
  alreadyLive,
  draftAhead,
  promoteError,
  onPromote,
  isViewer,
}) => {
  const [tab, setTab] = useState<PanelTab>('notes');
  const [confirming, setConfirming] = useState(false);
  useEffect(() => setConfirming(false), [env, promoteVersion]);
  // The version this publish sends out.
  const outgoing = promoteVersion ?? draftVersion;
  const actions = useActionFileDiff(baselineVersion, promoteVersion);
  const actionChanges = actions.changes ?? [];
  const notes = useReleaseNotes({
    version: outgoing,
    baseline: baselineVersion ? `${baselineVersion} · ${baselineEnvLabel}` : '',
    // Drafted once the action code is compared too, not twice.
    diff: actions.status === 'loading' ? null : diff,
    extra: describeActionChanges(actionChanges),
  });
  const total = diff ? totalChanges(diff.counts) : 0;
  const beforeLabel = baselineVersion
    ? `${baselineVersion} · ${baselineEnvLabel}`
    : 'Nothing published';
  const afterLabel = promoteVersion
    ? `${promoteVersion} · Sandbox`
    : `${draftVersion} · Draft`;
  const width = tab === 'diff' ? undefined : 'full';

  const publishButton = promoteVersion ? (
    <Button
      variant="primary"
      test-id="bobcat-deploy-promote"
      disabled={alreadyLive || !!promoteError || !diff}
      onClick={() => {
        if (!confirming) {
          setConfirming(true);
          return;
        }
        setConfirming(false);
        onPromote();
      }}
      className="c-deploy-panel__publish"
      width={width}
    >
      {confirming
        ? `Put ${promoteVersion} live`
        : `Promote ${promoteVersion} to Live`}
    </Button>
  ) : (
    <Button
      variant="primary"
      test-id="bobcat-deploy-publish"
      disabled={!canPublish}
      onClick={onPublish}
      className="c-deploy-panel__publish"
      width={width}
    >
      Publish {draftVersion} to {env === 'live' ? 'Live' : 'Sandbox'}
    </Button>
  );

  return (
    <aside
      className={`c-deploy-panel${tab === 'diff' ? ' is-wide' : ''}`}
      data-testid="bobcat-deploy-publish-panel"
    >
      <div className="c-deploy-panel__scroll">
        <TextTitle size="small">
          {promoteVersion
            ? `Promote ${promoteVersion} to Live`
            : `Publish ${draftVersion}`}
        </TextTitle>
        {promoteVersion && (
          <TextCaption variant="secondary">
            Live takes the version tested in Sandbox, not the draft.
          </TextCaption>
        )}
        <div className="c-deploy-panel__sub">
          {promoteError ? (
            <TextCaption variant="danger">
              Couldn&apos;t load {promoteVersion}: {promoteError}
            </TextCaption>
          ) : alreadyLive ? (
            <TextCaption variant="secondary">
              {promoteVersion} is already live. Publish the draft to Sandbox and
              test it to send out the next version.
            </TextCaption>
          ) : diffError ? (
            <TextCaption variant="danger">
              Couldn&apos;t load {baselineVersion} to compare: {diffError}
            </TextCaption>
          ) : !diff ? (
            <TextCaption variant="secondary">Comparing…</TextCaption>
          ) : baselineVersion ? (
            <>
              <TextCaption variant="secondary">
                {total} node{total === 1 ? '' : 's'}
                {actionChanges.length
                  ? ` and ${actionChanges.length} action script${
                      actionChanges.length === 1 ? '' : 's'
                    }`
                  : ''}{' '}
                changed since {baselineVersion}
              </TextCaption>
              <CountChips counts={diff.counts} />
            </>
          ) : (
            <TextCaption variant="secondary">
              First publish — {total} node{total === 1 ? '' : 's'}
            </TextCaption>
          )}
        </div>

        <BobcatTabGroup
          variant="flush"
          className="c-deploy-tabs"
          test-id="bobcat-deploy-tabs"
          onTabShow={(e: CustomEvent<{ name: string }>) =>
            setTab(e.detail.name === 'diff' ? 'diff' : 'notes')
          }
        >
          <Tab
            slot="tab"
            panel="notes"
            active={tab === 'notes'}
            test-id="bobcat-deploy-tab-notes"
          >
            <span className="c-deploy-tabs__tab">
              <Icon name="changelog" size="xsmall" />
              Version notes
            </span>
          </Tab>
          <Tab
            slot="tab"
            panel="diff"
            active={tab === 'diff'}
            test-id="bobcat-deploy-tab-diff"
          >
            <span className="c-deploy-tabs__tab">
              <Icon name="brackets" size="xsmall" />
              Diff
            </span>
          </Tab>
        </BobcatTabGroup>

        {diff && tab === 'notes' && (
          <>
            {notes.note || notes.loading ? (
              <div className="c-deploy-eyebrow c-deploy-eyebrow--ai">
                <Icon name="stars" size="xsmall" />
                AI-drafted version notes
              </div>
            ) : (
              <div className="c-deploy-eyebrow">Summary of changes</div>
            )}
            <p className="c-deploy-note" data-testid="bobcat-deploy-summary">
              {notes.note ??
                (notes.loading
                  ? 'Drafting the version notes…'
                  : summarizeDiff(diff))}
            </p>
            {promoteVersion && draftAhead > 0 && !alreadyLive && (
              <section
                className="c-deploy-valid c-deploy-valid--none"
                data-testid="bobcat-deploy-draft-ahead"
              >
                <Icon name="info" size="xsmall" />
                <div>
                  <b>
                    The draft has {draftAhead} change
                    {draftAhead === 1 ? '' : 's'} that {promoteVersion}{' '}
                    doesn&apos;t
                  </b>
                  <span>
                    They aren&apos;t part of this. To send them out, publish the
                    draft to Sandbox and test it first.
                  </span>
                </div>
                <button
                  type="button"
                  className="c-deploy-valid__open"
                  onClick={() => onEnvChange('sandbox')}
                >
                  Sandbox
                </button>
              </section>
            )}
            <ValidationCallout
              version={promoteVersion}
              sandboxVersion={sandboxVersion}
            />
            {diff.agents.length > 0 && (
              <>
                <Spacer size="small" />
                <div className="c-deploy-eyebrow">
                  What&apos;s changing, by agent
                </div>
                <AgentChanges agents={diff.agents} />
              </>
            )}
            <ActionCodeSection state={actions} baseline={baselineVersion} />
          </>
        )}
        {diff && tab === 'diff' && (
          <>
            <DiffView
              diff={diff}
              beforeLabel={beforeLabel}
              afterLabel={afterLabel}
            />
            <ActionCodeSection state={actions} baseline={baselineVersion} />
          </>
        )}
      </div>

      {!isViewer && (
        <div className="c-deploy-panel__foot">
          <div className="c-deploy-env">
            <span>Publish to</span>
            <div className="c-deploy-env__switch" role="radiogroup">
              {(['sandbox', 'live'] as DeployEnv[])
                .filter((e) => envs.includes(e))
                .map((e) => (
                  <button
                    key={e}
                    type="button"
                    role="radio"
                    aria-checked={env === e}
                    className={`c-deploy-env__option${env === e ? ' is-active' : ''}`}
                    data-testid={`bobcat-deploy-env-${e}`}
                    onClick={() => onEnvChange(e)}
                  >
                    <span
                      className={`c-deploy-env__dot c-deploy-env__dot--${e}`}
                    />
                    {e === 'live' ? 'Live' : 'Sandbox'}
                  </button>
                ))}
            </div>
          </div>
          {publishTooltip ? (
            <Tooltip content={publishTooltip}>{publishButton}</Tooltip>
          ) : (
            publishButton
          )}
        </div>
      )}
    </aside>
  );
};
